<!--
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts" context="module">
  let bpmnNodeSeq = 0
</script>

<script lang="ts">
  import { MarkupNode, renderBpmnToSvg } from '@hcengineering/text'

  export let node: MarkupNode
  export let preview = false

  // Diagrams on the same page must not share the ids of their arrow markers.
  const idPrefix = `bpmn-${bpmnNodeSeq++}`

  // The textual notation is the source: it is rendered here, and shown as-is when it
  // cannot be parsed so that nothing the author wrote gets hidden.
  $: source = (node.content ?? []).map((child) => child.text ?? '').join('\n')
  $: rendered = renderBpmnToSvg(source, { idPrefix })
</script>

{#if node && source.trim().length > 0}
  {#if rendered.svg !== undefined}
    <div class="bpmnPreview" class:preview>
      <!-- The svg comes from renderBpmnToSvg, which escapes every piece of user text -->
      <!-- eslint-disable-next-line svelte/no-at-html-tags -->
      {@html rendered.svg}
    </div>
  {:else}
    <pre class="proseCodeBlock"><code>{source}</code></pre>
    {#if rendered.error !== undefined}
      <div class="bpmnError">Line {rendered.error.line}: {rendered.error.message}</div>
    {/if}
  {/if}
{/if}

<style lang="scss">
  .bpmnPreview {
    display: flex;
    justify-content: flex-start;
    overflow-x: auto;
    margin: 0.5rem 0;

    :global(svg) {
      max-width: 100%;
      height: auto;
    }

    &.preview :global(svg) {
      max-height: 10rem;
    }
  }

  .bpmnError {
    margin-top: 0.25rem;
    color: var(--theme-error-color);
    font-size: 0.8125rem;
  }
</style>
